import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { aiSettingsApi, type AiProvider, type TranslationSettingsData, type TranslationStatus } from '../../lib/aiSettings';

export function TranslationSettingsCard({ dark, providers }: { dark: boolean; providers: AiProvider[] }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [s, setS] = useState<TranslationSettingsData | null>(null);
  const [status, setStatus] = useState<TranslationStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([aiSettingsApi.getSettings(), aiSettingsApi.status()])
      .then(([settings, st]) => { if (alive) { setS(settings); setStatus(st); } })
      .catch(() => { if (alive) setError(tr('settings.api.load_error')); });
    return () => { alive = false; };
  }, [tr]);

  const save = async () => {
    if (!s) return;
    setSaving(true); setError(null);
    try { setS(await aiSettingsApi.updateSettings(s)); } catch { setError(tr('settings.api.save_error')); } finally { setSaving(false); }
  };

  const retry = async () => {
    await aiSettingsApi.retryFailed();
    setStatus(await aiSettingsApi.status());
  };

  const selectStyle = { padding: '9px 12px', borderRadius: 9, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text } as const;
  // Modell/Typ an den Namen anhängen: unterscheidet Anbieter mit gleichem
  // Namen in der Auswahl und sorgt dafür, dass der sichtbare Options-Text nie
  // exakt dem reinen Anbieternamen der Anbieter-Liste gleicht.
  const providerOptionLabel = (p: AiProvider) => `${p.name} · ${p.model || tr(`settings.api.form.type_${p.type}`)}`;
  const providerSelect = (key: 'provider_id' | 'fallback_provider_id', label: string) => (
    <Field label={label}>
      <select aria-label={label} style={selectStyle} value={s?.[key] ?? ''}
        onChange={e => setS(v => v && { ...v, [key]: e.target.value ? Number(e.target.value) : null })}>
        <option value="">{tr('settings.api.translation.none')}</option>
        {providers.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{providerOptionLabel(p)}</option>)}
      </select>
    </Field>
  );

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle dark={dark} title={tr('settings.api.translation.title')} />
      {error && <div style={{ color: '#b42318', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {s && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: t.text }}>
            <input type="checkbox" checked={s.enabled} onChange={e => setS({ ...s, enabled: e.target.checked })} />
            {tr('settings.api.translation.enabled')}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {providerSelect('provider_id', tr('settings.api.translation.provider'))}
            {providerSelect('fallback_provider_id', tr('settings.api.translation.fallback'))}
          </div>
          <Field label={tr('settings.api.translation.instructions')} hint={tr('settings.api.translation.instructions_hint')}>
            <textarea aria-label={tr('settings.api.translation.instructions')} rows={3} value={s.instructions}
              onChange={e => setS({ ...s, instructions: e.target.value })}
              style={{ padding: 9, borderRadius: 9, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif' }} />
          </Field>
          <div><Button dark={dark} onClick={save} disabled={saving}>{tr('settings.api.translation.save')}</Button></div>
        </div>
      )}

      {status && (
        <div style={{ marginTop: 20, borderTop: `1px solid ${t.borderSoft}`, paddingTop: 14 }}>
          <div style={{ fontWeight: 600, color: t.heading, marginBottom: 8 }}>{tr('settings.api.status.title')}</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: t.text }}>
            {(['ok', 'pending', 'failed', 'stale'] as const).map(k => (
              <span key={k}>{tr(`settings.api.status.${k}`)}: <strong>{status.counts[k]}</strong></span>
            ))}
          </div>
          <div style={{ fontSize: 13, color: t.muted, marginTop: 6 }}>
            {status.backfill.done
              ? tr('settings.api.status.backfill_done')
              : tr('settings.api.status.backfill', { processed: status.backfill.processed, total: status.backfill.total })}
          </div>
          <div style={{ fontSize: 13, color: t.muted, marginTop: 4 }}>
            {tr('settings.api.status.usage', {
              requests: status.usage_month.requests, input: status.usage_month.input_units,
              output: status.usage_month.output_units, errors: status.usage_month.errors,
            })}
          </div>
          {status.counts.failed > 0 && (
            <div style={{ marginTop: 10 }}>
              <Button size="sm" variant="secondary" dark={dark} onClick={retry}>{tr('settings.api.status.retry')}</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
